import { useState, useMemo } from "react";
import { Link, useNavigate, useLocation } from "@tanstack/react-router";
import { ChevronDown, ChevronLeft } from "lucide-react";
import { trpc } from "@/hooks/trpc";
import { getDomainFromPath, domainSidebars } from "@/modules/navigation/config";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverAnchor,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandItem,
  CommandGroup,
  CommandEmpty,
  CommandSeparator,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

interface QuickSwitchRowProps {
  parentHref: string;
  parentSearch?: Record<string, string>;
  parentLabel: string;
}

type SwitchEntity = {
  id: number;
  name: string;
  label?: string | null;
  href: string;
  isActive?: boolean;
  startDate?: Date | string | null;
};

/**
 * Back-link row with optional quick-switch popover.
 * For supported domains (fermentation, harvest), renders a chevron-down trigger
 * that opens a searchable popup anchored below the full back-link row.
 * For unsupported domains, renders a plain back link.
 */
export function QuickSwitchRow({
  parentHref,
  parentSearch,
  parentLabel,
}: QuickSwitchRowProps) {
  const domainId = getDomainFromPath(parentHref);
  const supported = domainId === "fermentation" || domainId === "harvest";

  if (!supported) {
    return (
      <Link
        to={parentHref}
        search={parentSearch}
        className="flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground border-b border-olive-6"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
        <span>{parentLabel}</span>
      </Link>
    );
  }

  return (
    <QuickSwitchPopover
      domainId={domainId}
      parentHref={parentHref}
      parentLabel={parentLabel}
    />
  );
}

function QuickSwitchPopover({
  domainId,
  parentHref,
  parentLabel,
}: {
  domainId: "fermentation" | "harvest";
  parentHref: string;
  parentLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const onSelect = (href: string) => {
    navigate({ to: href });
    setOpen(false);
  };

  // Domain pages (static)
  const domainPages = useMemo(() => {
    const items = domainSidebars[domainId]?.items ?? [];
    return items.flatMap((item) =>
      item.type === "link" ? [{ label: item.label, href: item.href }] : [],
    );
  }, [domainId]);

  // Sibling entities (conditional hooks)
  const fermGroups = trpc.fermentations.groups.list.useQuery(undefined, {
    enabled: open && domainId === "fermentation",
  });
  const harvestSessions = trpc.harvest.listSessions.useQuery(undefined, {
    enabled: open && domainId === "harvest",
  });

  // Normalize entities
  const entities: SwitchEntity[] = useMemo(() => {
    if (domainId === "fermentation") {
      return (fermGroups.data ?? []).map((g) => ({
        id: g.id,
        name: g.name,
        label: g.label,
        href: `/fermentations/${g.id}`,
        isActive: g.is_active,
        startDate: g.start_date,
      }));
    }
    if (domainId === "harvest") {
      const rows = harvestSessions.data ?? [];
      const seen = new Map<number, SwitchEntity>();
      for (const row of rows) {
        if (row.session_id && !seen.has(row.session_id)) {
          seen.set(row.session_id, {
            id: row.session_id,
            name: row.session_name ?? `Harvest ${row.session_id}`,
            label: row.session_label,
            href: `/harvests/${row.session_id}/journal`,
          });
        }
      }
      return Array.from(seen.values());
    }
    return [];
  }, [domainId, fermGroups.data, harvestSessions.data]);

  // Split entities into active and completed, sorted by start date desc
  const { activeEntities, completedEntities } = useMemo(() => {
    const byStartDateDesc = (a: SwitchEntity, b: SwitchEntity) => {
      if (!a.startDate && !b.startDate) return 0;
      if (!a.startDate) return 1;
      if (!b.startDate) return -1;
      return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
    };
    const active = entities.filter((e) => e.isActive).sort(byStartDateDesc);
    const completed = entities.filter((e) => !e.isActive).sort(byStartDateDesc);
    return { activeEntities: active, completedEntities: completed };
  }, [entities]);

  // Current entity ID from pathname
  const currentEntityId = useMemo(() => {
    const afterDomain = location.pathname.replace(parentHref + "/", "");
    const segment = afterDomain.split("/")[0];
    const parsed = Number(segment);
    return Number.isNaN(parsed) ? null : parsed;
  }, [location.pathname, parentHref]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="flex items-center border-b border-olive-6">
          <Link
            to={parentHref}
            className="flex-1 flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            <span>{parentLabel}</span>
          </Link>
          <PopoverTrigger asChild>
            <button
              className="px-2 py-2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Quick switch"
            >
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={0}
        portal={false}
        className="w-[var(--radix-popover-anchor-width)] p-0"
      >
        <Command>
          <CommandInput placeholder="Search..." />
          <CommandList>
            <CommandEmpty>No results</CommandEmpty>
            <CommandGroup heading="Pages">
              {domainPages.map((page) => (
                <CommandItem
                  key={page.href}
                  value={page.label}
                  onSelect={() => onSelect(page.href)}
                  className="text-sm"
                >
                  {page.label}
                </CommandItem>
              ))}
            </CommandGroup>
            {activeEntities.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Active">
                  {activeEntities.map((entity) => (
                    <CommandItem
                      key={entity.id}
                      value={
                        entity.label
                          ? `${entity.name} ${entity.label}`
                          : entity.name
                      }
                      onSelect={() => onSelect(entity.href)}
                      className={cn(
                        "text-sm",
                        entity.id === currentEntityId &&
                          "text-primary font-medium",
                      )}
                    >
                      {entity.label
                        ? `${entity.name} · ${entity.label}`
                        : entity.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
            {completedEntities.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Completed">
                  {completedEntities.map((entity) => (
                    <CommandItem
                      key={entity.id}
                      value={
                        entity.label
                          ? `${entity.name} ${entity.label}`
                          : entity.name
                      }
                      onSelect={() => onSelect(entity.href)}
                      className={cn(
                        "text-sm",
                        entity.id === currentEntityId &&
                          "text-primary font-medium",
                      )}
                    >
                      {entity.label
                        ? `${entity.name} · ${entity.label}`
                        : entity.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
